import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { TERMS, CATEGORIES, categoryLabel } from "@/data/glossary";
import { PublicShell, SignupCta, useSeo } from "./PublicShell";

const matches = (t, q) => {
  const hay = [t.title, t.acronym, ...(t.aka || []), t.definition].join(" ").toLowerCase();
  return hay.includes(q);
};

export default function GlossaryIndex() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useSeo({
    title: "Canadian Autism & Neurodiversity Glossary | KidPath",
    description: "Plain-language definitions of autism, neurodiversity, therapy, school, and funding terms for Canadian families. Search and understand the words in your child's reports and applications.",
    jsonLd: {
      "@context": "https://schema.org", "@type": "DefinedTermSet",
      name: "KidPath Canadian Autism & Neurodiversity Glossary",
      url: "https://kidpath.ca/terms/",
      hasDefinedTerm: TERMS.map((t) => ({ "@type": "DefinedTerm", name: t.title, url: `https://kidpath.ca/terms/${t.slug}/` })),
    },
  });

  const query = q.trim().toLowerCase();
  const filtered = useMemo(
    () => TERMS.filter((t) => (cat === "all" || t.category === cat) && (!query || matches(t, query)))
      .sort((a, b) => a.title.localeCompare(b.title)),
    [query, cat]
  );

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((t) => { const l = t.title[0].toUpperCase(); (map[l] = map[l] || []).push(t); });
    return Object.keys(map).sort().map((letter) => ({ letter, items: map[letter] }));
  }, [filtered]);

  return (
    <PublicShell>
      {/* Hero */}
      <section className="bg-gradient-to-b from-kid-skytint/50 to-white border-b border-kid-line">
        <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-14 sm:py-16">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-kid-tealcta mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-kid-coral" /> Plain-language glossary
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold max-w-3xl leading-tight">Canadian Autism &amp; Neurodiversity Glossary</h1>
          <p className="text-kid-muted text-lg mt-4 max-w-2xl">Clear, supportive definitions of the terms you'll see in assessments, school plans, therapy, and funding applications — written for Canadian families.</p>

          <div className="mt-7 relative max-w-xl">
            <Search size={19} className="absolute left-4 top-1/2 -translate-y-1/2 text-kid-muted" />
            <input
              data-testid="glossary-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms, e.g. IEP, stimming, DTC…"
              className="w-full h-14 pl-12 pr-11 rounded-2xl border border-kid-line bg-white text-kid-navy placeholder:text-kid-muted/70 focus:outline-none focus:ring-2 focus:ring-kid-teal/50"
            />
            {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-kid-muted hover:text-kid-navy" data-testid="glossary-search-clear"><X size={18} /></button>}
          </div>
        </div>
      </section>

      <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        {/* Browse by category (SEO hubs) */}
        {!query && cat === "all" && (
          <section className="mb-10" data-testid="glossary-browse-categories">
            <h2 className="font-display text-xl font-extrabold mb-4">Browse by category</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CATEGORIES.map((c) => {
                const n = TERMS.filter((t) => t.category === c.key).length;
                return (
                  <Link key={c.key} to={`/terms/category/${c.key}`} data-testid={`glossary-hub-${c.key}`}
                    className="kid-card kid-card-interactive p-4 flex items-center justify-between gap-3">
                    <span className="font-semibold text-kid-navy">{c.label}</span>
                    <span className="text-xs font-semibold text-kid-tealcta bg-kid-skytint rounded-full px-2.5 py-1">{n}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="glossary-categories">
          <button onClick={() => setCat("all")} data-testid="glossary-cat-all"
            className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${cat === "all" ? "bg-kid-navy text-white border-kid-navy" : "border-kid-line text-kid-muted hover:text-kid-navy"}`}>All terms</button>
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`glossary-cat-${c.key}`}
              className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${cat === c.key ? "bg-kid-navy text-white border-kid-navy" : "border-kid-line text-kid-muted hover:text-kid-navy"}`}>{c.label}</button>
          ))}
        </div>

        <p className="text-sm text-kid-muted mb-6" data-testid="glossary-result-count">{filtered.length} {filtered.length === 1 ? "term" : "terms"}</p>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-kid-line p-10 text-kid-muted" data-testid="glossary-no-results">
            No terms match "{q}". Try a shorter word or an acronym like <b>IEP</b> or <b>OAP</b>.
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.map(({ letter, items }) => (
              <div key={letter}>
                <div className="font-display text-2xl font-extrabold text-kid-teal mb-3">{letter}</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((t) => (
                    <Link key={t.slug} to={`/terms/${t.slug}`} data-testid={`glossary-term-${t.slug}`}
                      className="kid-card kid-card-interactive p-5 block">
                      <div className="text-xs font-semibold uppercase tracking-wide text-kid-tealcta mb-1.5">{categoryLabel(t.category)}</div>
                      <div className="font-bold text-kid-navy text-lg leading-snug">{t.title}{t.acronym ? <span className="text-kid-muted font-semibold"> ({t.acronym})</span> : null}</div>
                      <p className="text-sm text-kid-muted mt-1.5 line-clamp-2">{t.definition}</p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12"><SignupCta variant="band" testid="glossary-index-cta" /></div>
      </div>
    </PublicShell>
  );
}
